import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Download, FileSpreadsheet, FileCheck2, PenLine, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { getErrorMessage } from "@/lib/errors";
import {
  haciendaExportarExcel, haciendaGenerarCenso, haciendaListarCensos, haciendaFirmarCenso, haciendaDescargarCensoPdf,
} from "@/api";

const SERVICIOS = [
  { value: "enseres", label: "Enseres" },
  { value: "poda", label: "Poda" },
  { value: "puntolimpio", label: "Punto Limpio Móvil" },
];

const anioActual = new Date().getFullYear();

export default function HaciendaPanel() {
  const [anio, setAnio] = useState(anioActual + 1);
  const [exportando, setExportando] = useState("");
  const [censoServicio, setCensoServicio] = useState("enseres");
  const [censoAnio, setCensoAnio] = useState(anioActual + 1);
  const [generando, setGenerando] = useState(false);
  const [censos, setCensos] = useState([]);
  const [firmarId, setFirmarId] = useState(null);
  const [password, setPassword] = useState("");
  const [firmando, setFirmando] = useState(false);

  const loadCensos = () => haciendaListarCensos().then(setCensos).catch(() => {});

  useEffect(() => { loadCensos(); }, []);

  const handleExport = async (servicio) => {
    setExportando(servicio);
    try {
      await haciendaExportarExcel(servicio, anio);
      toast.success("Excel descargado");
    } catch (err) {
      toast.error(getErrorMessage(err, "No se pudo exportar el Excel"));
    } finally {
      setExportando("");
    }
  };

  const handleGenerar = async () => {
    setGenerando(true);
    try {
      await haciendaGenerarCenso(censoServicio, Number(censoAnio));
      toast.success("Censo de Medio Ambiente generado (borrador)");
      loadCensos();
    } catch (err) {
      toast.error(getErrorMessage(err, "No se pudo generar el censo"));
    } finally {
      setGenerando(false);
    }
  };

  const handleFirmar = async () => {
    setFirmando(true);
    try {
      await haciendaFirmarCenso(firmarId, password);
      toast.success("Censo firmado correctamente");
      setFirmarId(null);
      setPassword("");
      loadCensos();
    } catch (err) {
      toast.error(getErrorMessage(err, "No se pudo firmar el censo"));
    } finally {
      setFirmando(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 flex items-start gap-2 text-sm text-amber-800" data-testid="hacienda-aviso">
        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
        Estos ficheros son documentos de apoyo. El cruce definitivo de identidad/domicilio y la aplicación
        final de la bonificación corresponde en exclusiva al Ayuntamiento conforme a su propio censo/padrón municipal.
      </div>

      <div className="bg-white rounded-xl shadow-sm p-5">
        <h3 className="font-heading font-semibold text-municipal mb-1 flex items-center gap-2">
          <FileSpreadsheet className="w-4 h-4" /> Exportaciones Excel para Hacienda
        </h3>
        <p className="text-xs text-gray-400 mb-4">
          Un fichero por servicio, con nombre, apellidos, DNI, dirección completa, origen de la
          bonificación y año de aplicación. Las acciones del año {Number(anio) - 1} bonifican la tasa del año {anio}.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs mb-1 block">Año de bonificación</Label>
            <Input type="number" value={anio} onChange={(e) => setAnio(e.target.value)} className="w-32" data-testid="hacienda-anio-input" />
          </div>
          {SERVICIOS.map((s) => (
            <Button
              key={s.value}
              variant="outline"
              className="border-municipal text-municipal"
              disabled={exportando === s.value}
              onClick={() => handleExport(s.value)}
              data-testid={`hacienda-export-${s.value}-btn`}
            >
              {exportando === s.value ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              {s.label}
            </Button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-5">
        <h3 className="font-heading font-semibold text-municipal mb-1 flex items-center gap-2">
          <FileCheck2 className="w-4 h-4" /> Censo de Medio Ambiente
        </h3>
        <p className="text-xs text-gray-400 mb-4">
          Genera el PDF del censo para un servicio y año, y fírmalo electrónicamente (firma simple) como
          técnico/a de Medio Ambiente para incorporarlo al expediente administrativo.
        </p>
        <div className="flex flex-wrap items-end gap-3 mb-5">
          <div>
            <Label className="text-xs mb-1 block">Servicio</Label>
            <Select value={censoServicio} onValueChange={setCensoServicio}>
              <SelectTrigger className="w-44" data-testid="censo-servicio-select"><SelectValue /></SelectTrigger>
              <SelectContent>{SERVICIOS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs mb-1 block">Año</Label>
            <Input type="number" value={censoAnio} onChange={(e) => setCensoAnio(e.target.value)} className="w-28" data-testid="censo-anio-input" />
          </div>
          <Button onClick={handleGenerar} disabled={generando} className="bg-municipal hover:bg-municipal-dark text-white" data-testid="censo-generar-btn">
            {generando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Generar censo (borrador)
          </Button>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Servicio</TableHead>
              <TableHead>Año</TableHead>
              <TableHead>Registros</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead>Firmado por</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {censos.map((c) => (
              <TableRow key={c.id} data-testid={`censo-row-${c.id}`}>
                <TableCell className="capitalize">{c.servicio === "puntolimpio" ? "Punto Limpio Móvil" : c.servicio}</TableCell>
                <TableCell>{c.anio}</TableCell>
                <TableCell>{c.total_registros}</TableCell>
                <TableCell>
                  <Badge className={c.estado === "firmado" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}>
                    {c.estado === "firmado" ? "Firmado" : "Borrador"}
                  </Badge>
                </TableCell>
                <TableCell className="text-xs text-gray-500">{c.firmado_por || "—"}</TableCell>
                <TableCell className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => haciendaDescargarCensoPdf(c.id, c.servicio, c.anio)} data-testid={`censo-download-${c.id}`}>
                    <Download className="w-4 h-4" />
                  </Button>
                  {c.estado !== "firmado" && (
                    <Button variant="ghost" size="sm" onClick={() => setFirmarId(c.id)} className="text-municipal" data-testid={`censo-firmar-${c.id}`}>
                      <PenLine className="w-4 h-4" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {censos.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-gray-400 py-8">Todavía no se ha generado ningún censo.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!firmarId} onOpenChange={(v) => !v && setFirmarId(null)}>
        <DialogContent className="max-w-sm" data-testid="censo-firmar-dialog">
          <DialogHeader>
            <DialogTitle className="text-municipal font-heading">Firma electrónica simple</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-500 mb-2">
            Confirma tu contraseña para firmar este censo como técnico/a de Medio Ambiente. Quedará
            registrado tu nombre, fecha y la huella de integridad del documento.
          </p>
          <Label className="text-xs mb-1 block">Contraseña</Label>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="censo-firmar-password-input" />
          <Button onClick={handleFirmar} disabled={!password || firmando} className="w-full bg-municipal hover:bg-municipal-dark text-white mt-3" data-testid="censo-firmar-confirmar-btn">
            {firmando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Firmar censo
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
